import { zodResolver } from "@hookform/resolvers/zod"
import { skipToken } from "@tanstack/react-query"
import { Image, Pen, Trash2, User } from "lucide-react"
import type React from "react"
import { useEffect, useState } from "react"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import z from "zod"
import { PageLoader } from "@/components/page-loader"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { FileInput } from "@/components/ui/file-input"
import { FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Typography } from "@/components/ui/typography"
import { useAuth } from "@/hooks/use-auth"
import { trpc } from "@/lib/trpc"
import { Label } from "../ui/label"

const userFormSchema = z.object({
  firstName: z.string().min(1, "Required"),
  lastName: z.string().min(1, "Required"),
  dateOfDeath: z.string().optional(),
  estatePhase: z.string().optional(),
  country: z.string(),
  stateProvince: z.string().optional(),
  receiveReferrals: z.boolean(),
})

type UserFormValues = z.infer<typeof userFormSchema>

const BRAND_LOGO_MIME_TYPES = ["image/png", "image/jpeg", "image/svg+xml"] as const
type BrandLogoMimeType = (typeof BRAND_LOGO_MIME_TYPES)[number]

function isBrandLogoMimeType(value: string): value is BrandLogoMimeType {
  return (BRAND_LOGO_MIME_TYPES as readonly string[]).includes(value)
}

export default function Profile() {
  const { t } = useTranslation()
  const [avatar, setAvatar] = useState<File | null>(null)
  const [brandLogo, setBrandLogo] = useState<File | null>(null)
  const { signOut } = useAuth()
  const queryUtils = trpc.useUtils()

  const { data: user } = trpc.users.me.useQuery()
  const { data: professionalBrandLogos } = trpc.users.listProfessionalBrandLogos.useQuery(
    user?.userType === "professional" ? undefined : skipToken,
  )
  const existingBrandLogo = professionalBrandLogos?.logos[0] ?? null

  const { data: location } = trpc.listLocations.useQuery(
    user?.estateId ? { estateId: user.estateId } : skipToken,
  )

  const deleteAccount = trpc.users.delete.useMutation({
    onSuccess: async () => {
      toast.success("Account deleted successfully")
      await signOut()
    },
    onError: () => toast.error("Error deleting account"),
  })

  const { data: estate } = trpc.estates.getById.useQuery(
    user?.estateId ? { estateId: user.estateId } : skipToken,
  )

  const contactUpdate = trpc.contacts.update.useMutation({
    onError: () => toast.error("Error updating contact."),
  })

  const updateEstatePhase = trpc.estates.updateEstatePhase.useMutation({
    onError: () => toast.error("Error updating estate phase."),
  })

  const updateLocation = trpc.updateLocation.useMutation({
    onError: () => toast.error("Error updating user."),
  })

  const updatePersonalBrandLogo = trpc.users.updatePersonalBrandLogo.useMutation({
    onSuccess: async (data) => {
      if (!brandLogo) {
        toast.error(t("components.profile.brandLogoSelectRequired"))
        return
      }

      const uploadResponse = await fetch(data.uploadUrl, {
        method: "PUT",
        body: brandLogo,
        headers: {
          "Content-Type": brandLogo.type,
        },
      })

      if (!uploadResponse.ok) {
        toast.error(t("components.profile.brandLogoUploadFailed"))
        return
      }

      await queryUtils.users.listProfessionalBrandLogos.invalidate()
      setBrandLogo(null)
      toast.success(t("components.profile.brandLogoUploadSuccess"))
    },
    onError: () => toast.error(t("components.profile.brandLogoUpdateError")),
  })

  const deletePersonalBrandLogo = trpc.users.deletePersonalBrandLogo.useMutation({
    onSuccess: async () => {
      await queryUtils.users.listProfessionalBrandLogos.invalidate()
      setBrandLogo(null)
      toast.success(t("components.profile.brandLogoDeleteSuccess"))
    },
    onError: () => toast.error(t("components.profile.brandLogoDeleteError")),
  })

  const form = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      dateOfDeath: user?.dateOfDeath ?? "",
      estatePhase: "PLANNING",
      country: "USA",
      stateProvince: "",
      receiveReferrals: true,
    },
  })

  useEffect(() => {
    if (user && location?.length) {
      form.reset({
        firstName: user.firstName ?? "",
        lastName: user.lastName ?? "",
        dateOfDeath: user.dateOfDeath ?? "",
        estatePhase: estate?.estatePhase ?? "PLANNING",
        country: location[0]?.country ?? "",
        stateProvince: location[0]?.city ?? "",
      })
    }
  }, [user, location, estate, form])

  const handleSaveChanges = async (data: UserFormValues) => {
    if (!user) return

    const promises: Promise<unknown>[] = []

    // 1. Save contact fields (dateOfDeath, firstName, lastName)
    if (user.id) {
      promises.push(
        contactUpdate.mutateAsync({
          contactId: user.id,
          firstName: data.firstName,
          lastName: data.lastName,
          dateOfDeath: data.dateOfDeath ?? null,
        }),
      )
    }

    // 2. Save location (existing behaviour)
    if (location?.[0]) {
      promises.push(
        updateLocation.mutateAsync({
          county: data.country,
          city: data.stateProvince,
          locationId: location[0].id,
        }),
      )
    }

    // 3. Save estate phase
    if (user.estateId && data.estatePhase) {
      promises.push(
        updateEstatePhase.mutateAsync({
          estateId: user.estateId,
          estatePhase: data.estatePhase as "PLANNING" | "PROBATE" | "SETTLED",
        }),
      )
    }

    try {
      await Promise.all(promises)
      toast.success("Profile updated successfully")
    } catch {
      toast.error("Error updating profile")
    }
  }

  const handleDeleteAccount = () => {
    deleteAccount.mutate()
  }

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    setAvatar(file)
  }

  const handleUploadBrandLogo = async () => {
    if (!user || user.userType !== "professional") return
    if (existingBrandLogo) {
      toast.error(t("components.profile.brandLogoDeleteFirst"))
      return
    }
    if (!brandLogo) {
      toast.error(t("components.profile.brandLogoSelectRequired"))
      return
    }
    if (!brandLogo.type) {
      toast.error(t("components.profile.brandLogoTypeUnknown"))
      return
    }
    if (!isBrandLogoMimeType(brandLogo.type)) {
      toast.error(t("components.profile.brandLogoInvalidType"))
      return
    }

    await updatePersonalBrandLogo.mutateAsync({
      fileName: brandLogo.name,
      contentType: brandLogo.type,
    })
  }

  const handleDeleteBrandLogo = async () => {
    await deletePersonalBrandLogo.mutateAsync()
  }

  if (!user) {
    return <PageLoader />
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSaveChanges)} className="min-h-max bg-background p-6">
        <div className="mx-auto space-y-8">
          <Typography as="h1" className="text-3xl font-semibold text-primary">
            My Profile
          </Typography>

          {/* Personal Info */}
          <Card className="p-8 rounded-xl shadow-sm border border-border space-y-6">
            <Typography as="h2" className="text-xl font-medium text-primary">
              Personal Information
            </Typography>

            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="relative">
                <Avatar className="w-32 h-32">
                  {avatar ? (
                    <AvatarImage src={URL.createObjectURL(avatar)} />
                  ) : (
                    <AvatarFallback className="bg-blue-100 text-4xl">
                      <User className="w-full h-full" />
                    </AvatarFallback>
                  )}
                </Avatar>

                <Button
                  size="sm"
                  className="absolute -bottom-1 -right-1 w-8 h-8 p-0 rounded-full bg-white shadow flex items-center justify-center"
                >
                  <Label htmlFor="avatar">
                    <Pen className="w-4 h-4 text-black" />
                  </Label>
                  <Input id="avatar" type="file" onChange={handleAvatarChange} className="hidden" />
                </Button>
              </div>

              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <Controller
                    name="firstName"
                    control={form.control}
                    render={({ field }) => <Input {...field} className="py-3 border-border" />}
                  />
                  <FormMessage>{form.formState.errors.firstName?.message}</FormMessage>
                </FormItem>

                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <Controller
                    name="lastName"
                    control={form.control}
                    render={({ field }) => <Input {...field} className="py-3 border-border" />}
                  />
                  <FormMessage>{form.formState.errors.lastName?.message}</FormMessage>
                </FormItem>

                <FormItem>
                  <FormLabel>Date of Death (optional)</FormLabel>
                  <Controller
                    name="dateOfDeath"
                    control={form.control}
                    render={({ field }) => (
                      <Input {...field} type="date" className="py-3 border-border" />
                    )}
                  />
                </FormItem>
              </div>
            </div>

            {user.userType === "professional" ? (
              <div className="border-t border-border pt-6 space-y-3">
                <Typography as="h3" className="text-base font-medium text-primary">
                  Professional Branding
                </Typography>
                {existingBrandLogo ? (
                  <div className="flex items-center justify-between rounded-lg border border-border bg-white p-4">
                    <div className="flex min-w-0 items-center gap-3">
                      <img
                        src={existingBrandLogo}
                        alt={t("components.profile.brandLogoExisting")}
                        className="h-10 w-10 rounded border border-border object-cover"
                      />
                      <Typography as="span" className="truncate text-sm">
                        {t("components.profile.brandLogoExisting")}
                      </Typography>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={handleDeleteBrandLogo}
                      disabled={
                        deletePersonalBrandLogo.isPending || updatePersonalBrandLogo.isPending
                      }
                    >
                      <Trash2 className="mr-1 h-4 w-4" />
                      {deletePersonalBrandLogo.isPending
                        ? t("components.profile.brandLogoDeleting")
                        : t("common.delete")}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <FormLabel className="flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      Upload brand logo
                    </FormLabel>
                    <FileInput
                      value={brandLogo}
                      onChange={(file) => {
                        setBrandLogo(file)
                      }}
                      accept=".png,.jpg,.jpeg,.svg"
                      disabled={
                        updatePersonalBrandLogo.isPending || deletePersonalBrandLogo.isPending
                      }
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleUploadBrandLogo}
                      disabled={
                        !brandLogo ||
                        updatePersonalBrandLogo.isPending ||
                        deletePersonalBrandLogo.isPending
                      }
                    >
                      {updatePersonalBrandLogo.isPending
                        ? t("components.profile.brandLogoUploading")
                        : t("components.profile.brandLogoUploadButton")}
                    </Button>
                    {brandLogo ? (
                      <Typography as="h2" className="text-sm text-muted-foreground">
                        Selected: {brandLogo.name}
                      </Typography>
                    ) : null}
                  </div>
                )}
              </div>
            ) : null}
          </Card>

          {/* Estate Details */}
          {user.estateId && (
            <Card className="p-8 rounded-xl shadow-sm border border-border space-y-4">
              <Typography as="h2" className="text-xl font-medium text-primary">
                Estate Details
              </Typography>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormItem>
                  <FormLabel>Estate Phase</FormLabel>
                  <Controller
                    name="estatePhase"
                    control={form.control}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className="py-3 border-border">
                          <SelectValue placeholder="Select estate phase" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PLANNING">Planning</SelectItem>
                          <SelectItem value="PROBATE">Probate</SelectItem>
                          <SelectItem value="SETTLED">Settled</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </FormItem>
              </div>
            </Card>
          )}

          {/* Address */}
          <Card className="p-8 rounded-xl shadow-sm border border-border space-y-4">
            <Typography as="h3" className="text-lg font-medium text-primary">
              Address
            </Typography>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormItem>
                <FormLabel>Country</FormLabel>
                <Controller
                  name="country"
                  control={form.control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="py-3 border-border">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="United States">🇺🇸 USA</SelectItem>
                        <SelectItem value="Canada">🇨🇦 Canada</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </FormItem>

              <FormItem>
                <FormLabel>State / Province</FormLabel>
                <Controller
                  name="stateProvince"
                  control={form.control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      className="py-3 border-border"
                      placeholder="Enter state or province"
                    />
                  )}
                />
              </FormItem>
            </div>
          </Card>

          {/* Checkbox */}
          <Controller
            name="receiveReferrals"
            control={form.control}
            render={({ field }) => (
              <div className="flex items-center space-x-3">
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked) => field.onChange(checked)}
                />
                <FormLabel className="text-sm font-medium text-muted-foreground cursor-pointer">
                  Receive Professional Services Referrals?
                </FormLabel>
              </div>
            )}
          />

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <Button type="submit">Save Changes</Button>
            <div className="flex gap-3">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructiveOutline" className="flex items-center gap-2">
                    <Trash2 className="w-4 h-4" />
                    Delete Account
                  </Button>
                </AlertDialogTrigger>

                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete your account and
                      remove your data.
                    </AlertDialogDescription>
                  </AlertDialogHeader>

                  <AlertDialogFooter className="flex flex-col sm:flex-row sm:justify-end gap-3 mt-6">
                    <AlertDialogCancel asChild>
                      <Button variant="outline" className="w-full sm:w-auto">
                        Cancel
                      </Button>
                    </AlertDialogCancel>
                    <AlertDialogAction asChild>
                      <Button
                        variant="destructiveOutline"
                        onClick={handleDeleteAccount}
                        className="w-full sm:w-auto"
                      >
                        Delete Account
                      </Button>
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}
